import React, { useState, useEffect, useRef } from 'react';
import './PhotoUpload.css';
import Icon from '../../../components/AppIcon';

const PhotoUpload = ({ 
  photos, 
  existingPhotos = [], 
  onPhotosChange, 
  onExistingPhotosChange,
  maxPhotos = 8,
  isEditMode = false 
}) => {
  const [allPhotos, setAllPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [photoSearch, setPhotoSearch] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartIndex, setDragStartIndex] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  
  const fileInputRef = useRef(null);

  // Normaliser l'URL de l'image
  const normalizeImageUri = (image) => {
    if (!image) return null;
    
    if (image.uri) return image.uri;
    
    if (typeof image === 'string') {
      if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('data:')) {
        return image;
      }
      if (image.startsWith('/')) {
        return `https://api.autocasses.com${image}`;
      }
      return `https://api.autocasses.com/${image}`;
    }
    
    if (image.url) {
      return normalizeImageUri(image.url);
    }
    
    return null;
  };

  useEffect(() => {
    const normalizeExistingPhotos = () => {
      return existingPhotos.map((photo, index) => {
        const uri = normalizeImageUri(photo);
        
        if (photo && typeof photo === 'object' && photo.uri) {
          return {
            ...photo,
            id: `existing_${Date.now()}_${index}`,
            isExisting: true,
            uri: photo.uri
          };
        }
        
        return {
          id: `existing_${Date.now()}_${index}`,
          uri: uri,
          isExisting: true,
          fileName: (photo && typeof photo === 'object' && photo.name) 
            ? photo.name 
            : `Photo existante ${index + 1}`,
          type: 'image/jpeg',
          ...(typeof photo === 'object' ? photo : {})
        };
      });
    };

    const formattedExisting = normalizeExistingPhotos();
    const combined = [...formattedExisting, ...photos];
    setAllPhotos(combined);
  }, [photos, existingPhotos]);

  // Gestion du drop de fichiers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (allPhotos.length >= maxPhotos) {
      alert(`Vous ne pouvez ajouter que ${maxPhotos} photos maximum.`);
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (useCamera = false) => {
    if (useCamera) {
      // Pour le web, on peut utiliser l'API camera directement
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const constraints = { video: { facingMode: 'environment' } };
        
        navigator.mediaDevices.getUserMedia(constraints)
          .then((stream) => {
            // Créer un élément video pour afficher le flux
            const video = document.createElement('video');
            video.srcObject = stream;
            video.play();
            
            // Créer un canvas pour capturer la photo
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            
            video.addEventListener('canplay', () => {
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              context.drawImage(video, 0, 0, canvas.width, canvas.height);
              
              // Convertir en blob
              canvas.toBlob((blob) => {
                const url = URL.createObjectURL(blob);
                const newPhoto = {
                  id: Date.now(),
                  uri: url,
                  fileName: `photo_${Date.now()}.jpg`,
                  fileSize: blob.size,
                  width: canvas.width,
                  height: canvas.height,
                  type: 'image/jpeg',
                  alt: `Photo ${allPhotos.length + 1}`,
                  timestamp: new Date().toISOString(),
                  isExisting: false,
                  isPrimary: allPhotos.length === 0
                };
                
                onPhotosChange([...photos, newPhoto]);
                
                // Arrêter le flux
                stream.getTracks().forEach(track => track.stop());
              }, 'image/jpeg');
            });
          })
          .catch((error) => {
            console.error('Camera error:', error);
            alert('Impossible d\'accéder à la caméra. Vérifiez vos permissions.');
          });
      } else {
        alert('Votre navigateur ne supporte pas l\'accès à la caméra.');
      }
    } else {
      // Ouvrir le sélecteur de fichiers
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
    e.target.value = ''; // Réinitialiser l'input
  };

  const handleFiles = (files) => {
    if (allPhotos.length >= maxPhotos) {
      alert(`Vous ne pouvez ajouter que ${maxPhotos} photos maximum.`);
      return;
    }

    const availableSlots = maxPhotos - allPhotos.length;
    const filesToProcess = files.slice(0, availableSlots);

    const newPhotos = filesToProcess.map((file, index) => {
      const reader = new FileReader();
      
      return new Promise((resolve) => {
        reader.onload = (e) => {
          const photo = {
            id: Date.now() + index,
            uri: e.target.result,
            fileName: file.name,
            fileSize: file.size,
            width: 0, // À déterminer après chargement
            height: 0,
            type: file.type,
            alt: `Photo ${allPhotos.length + index + 1}`,
            timestamp: new Date().toISOString(),
            isExisting: false,
            isPrimary: allPhotos.length === 0 && index === 0
          };
          resolve(photo);
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(newPhotos)
      .then((processedPhotos) => {
        // Récupérer les dimensions des images
        processedPhotos.forEach((photo, index) => {
          const img = new Image();
          img.onload = () => {
            photo.width = img.width;
            photo.height = img.height;
            
            // Mettre à jour la photo avec les dimensions
            processedPhotos[index] = photo;
            
            // Si toutes les images sont chargées, mettre à jour l'état
            if (index === processedPhotos.length - 1) {
              onPhotosChange([...photos, ...processedPhotos]);
            }
          };
          img.src = photo.uri;
        });
      });
  };

  const removePhoto = (index) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette photo ?')) {
      const photo = allPhotos[index];
      if (photo.isExisting) {
        const photoIndex = existingPhotos.findIndex(p => {
          const existingUri = normalizeImageUri(p);
          return existingUri === photo.uri;
        });
        const updatedExisting = existingPhotos.filter((_, i) => i !== photoIndex);
        if (onExistingPhotosChange) {
          onExistingPhotosChange(updatedExisting);
        }
      } else {
        const newIndex = photos.findIndex(p => p.id === photo.id);
        const updatedPhotos = photos.filter((_, i) => i !== newIndex);
        onPhotosChange(updatedPhotos);
      }
      setShowActionModal(false);
    }
  };

  const setPrimaryPhoto = (index) => {
    if (index === 0) return;
    
    const photo = allPhotos[index];
    
    if (photo.isExisting) {
      alert('La photo principale ne peut pas être modifiée en mode édition');
      return;
    }
    
    const photoIndex = photos.findIndex(p => p.id === photo.id);
    if (photoIndex === -1) return;
    
    const newPhotos = [...photos];
    const [removedPhoto] = newPhotos.splice(photoIndex, 1);
    removedPhoto.isPrimary = true;
    newPhotos.forEach(p => p.isPrimary = false);
    newPhotos.unshift(removedPhoto);
    onPhotosChange(newPhotos);
    setShowActionModal(false);
  };

  const reorderPhotos = (fromIndex, toIndex) => {
    if (isEditMode && existingPhotos.length > 0) {
      alert('La réorganisation n\'est disponible que pour les nouvelles photos');
      return;
    }
    
    const newPhotos = [...photos];
    const [removed] = newPhotos.splice(fromIndex, 1);
    newPhotos.splice(toIndex, 0, removed);
    onPhotosChange(newPhotos);
    setShowActionModal(false);
  };

  const handlePhotoPress = (photo, index) => {
    setSelectedPhoto(photo);
    setSelectedPhotoIndex(index);
    setShowImageModal(true);
  };

  const openActionMenu = (photo, index) => {
    setSelectedPhoto(photo);
    setSelectedPhotoIndex(index);
    setShowActionModal(true);
  };

  const filteredPhotos = photoSearch.trim() === '' 
    ? allPhotos 
    : allPhotos.filter(photo => 
        photo.fileName?.toLowerCase().includes(photoSearch.toLowerCase()) ||
        photo.alt?.toLowerCase().includes(photoSearch.toLowerCase())
      );

  const renderPhotoItem = (photo, index) => {
    const isExisting = photo.isExisting;
    const isPrimary = index === 0 || photo.isPrimary;
    
    return (
      <div
        key={photo.id}
        className={`photo-container ${isDragging && dragStartIndex === index ? 'dragging-photo' : ''} ${isPrimary ? 'primary-photo-container' : ''}`}
        onClick={() => handlePhotoPress(photo, index)}
        onDragStart={() => {
          setIsDragging(true);
          setDragStartIndex(index);
        }}
        onDragEnd={() => {
          setIsDragging(false);
          setDragStartIndex(null);
        }}
        draggable={!isExisting}
      >
        <img
          src={photo.uri}
          alt={photo.alt || `Photo ${index + 1}`}
          className="photo"
          onError={(e) => {
            console.log('Image loading error:', e);
            e.target.src = 'https://via.placeholder.com/150?text=Erreur+Image';
          }}
        />
        
        <div className="photo-overlay">
          <div className="badge-container">
            {isPrimary && (
              <div className="primary-badge">
                <Icon name="Star" size={12} color="white" />
                <span className="primary-badge-text">Principale</span>
              </div>
            )}
            
            {isExisting && (
              <div className="existing-badge">
                <span className="existing-badge-text">Existant</span>
              </div>
            )}
          </div>
          
          <div className="photo-number-badge">
            <span className="photo-number-text">{index + 1}</span>
          </div>
        </div>
        
        <button
          className="photo-action-button"
          onClick={(e) => {
            e.stopPropagation();
            openActionMenu(photo, index);
          }}
          type="button"
        >
          <Icon name="MoreVertical" size={20} color="white" />
        </button>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className="empty-container">
      <Icon name="ImageOff" size={60} color="#D1D5DB" />
      <h3 className="empty-title">Aucune photo</h3>
      <p className="empty-text">
        Ajoutez des photos pour montrer votre produit sous tous ses angles
      </p>
    </div>
  );

  const totalPhotos = allPhotos.length;
  const canAddMore = totalPhotos < maxPhotos;

  return (
    <div className="photo-upload-container">
      <div className="photo-upload-header">
        <div className="header-content">
          <h2 className="header-title">📸 Photos du Produit</h2>
          <p className="header-subtitle">
            Montrez votre produit sous tous ses angles
          </p>
        </div>
        <div className="stats-container">
          <div className="stat-item">
            <Icon name="ImageMultiple" size={16} color="#3B82F6" />
            <span className="stat-text">{totalPhotos}/{maxPhotos}</span>
          </div>
          {isEditMode && (
            <div className="stat-item">
              <Icon name="Pencil" size={16} color="#059669" />
              <span className="stat-text">Mode Édition</span>
            </div>
          )}
        </div>
      </div>

      <div className="upload-section">
        <div 
          className={`upload-card ${isDragOver ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Icon name="CloudUpload" size={48} color="#3B82F6" className="upload-icon" />
          
          <div className="upload-content">
            <h3 className="upload-title">
              {canAddMore ? 'Ajouter des photos' : 'Limite atteinte'}
            </h3>
            <p className="upload-description">
              {canAddMore 
                ? 'Sélectionnez ou prenez des photos de haute qualité'
                : `Maximum ${maxPhotos} photos atteint`}
            </p>
            
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${(totalPhotos / maxPhotos) * 100}%` }}
                />
              </div>
              <span className="progress-text">
                {totalPhotos} sur {maxPhotos} photos
              </span>
            </div>
          </div>
          
          <div className="upload-buttons">
            <button
              className="upload-button"
              onClick={() => handleFileSelect(false)}
              disabled={!canAddMore}
              type="button"
            >
              <Icon name="Image" size={20} color="#64748B" />
              <span className="quick-action-text">Galerie</span>
            </button>
            
            <button
              className="upload-button"
              onClick={() => handleFileSelect(true)}
              disabled={!canAddMore}
              type="button"
            >
              <Icon name="Camera" size={20} color="#64748B" />
              <span className="quick-action-text">Caméra</span>
            </button>
          </div>
          
          <p className="drag-drop-text">
            ou glissez-déposez vos images ici
          </p>
        </div>
        
        {/* Input file caché */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/*"
          multiple
          onChange={handleFileInputChange}
        />
      </div>

      {allPhotos.length > 0 && (
        <div className="search-container">
          <Icon name="Search" size={20} color="#6B7280" className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher parmi les photos..."
            value={photoSearch}
            onChange={(e) => setPhotoSearch(e.target.value)}
          />
          {photoSearch.length > 0 && (
            <button
              className="clear-search-button"
              onClick={() => setPhotoSearch('')}
              type="button"
            >
              <Icon name="XCircle" size={20} color="#9CA3AF" />
            </button>
          )}
        </div>
      )}

      {allPhotos.length > 0 ? (
        <div className="photos-section">
          <h3 className="section-title">
            <Icon name="Grid" size={16} color="#6B7280" /> 
            Toutes les photos
          </h3>
          
          <div className="photo-grid">
            {filteredPhotos.map((photo, index) => (
              <div key={photo.id} className="photo-grid-item">
                {renderPhotoItem(photo, index)}
              </div>
            ))}
          </div>
          
          {filteredPhotos.length === 0 && (
            <div className="empty-search-container">
              <Icon name="Search" size={40} color="#D1D5DB" />
              <p className="empty-search-text">
                Aucune photo trouvée
              </p>
            </div>
          )}
        </div>
      ) : (
        renderEmptyState()
      )}

      <div className="tips-container">
        <div className="tips-header">
          <Icon name="Lightbulb" size={20} color="#F59E0B" />
          <h4 className="tips-title">
            Conseils photo professionnels
          </h4>
        </div>
        
        <div className="tips-scroll-container">
          <div className="tip-card">
            <Icon name="Star" size={20} color="#F59E0B" />
            <h5 className="tip-card-title">
              Photo Principale
            </h5>
            <p className="tip-card-text">
              Utilisez l'image la plus attractive comme première photo
            </p>
          </div>
          
          <div className="tip-card">
            <Icon name="Camera" size={20} color="#F59E0B" />
            <h5 className="tip-card-title">
              Bonne Lumière
            </h5>
            <p className="tip-card-text">
              Photographiez en plein jour avec une lumière naturelle
            </p>
          </div>
          
          <div className="tip-card">
            <Icon name="Maximize" size={20} color="#F59E0B" />
            <h5 className="tip-card-title">
              Multiples Angles
            </h5>
            <p className="tip-card-text">
              Montrez le produit sous différents angles
            </p>
          </div>
          
          <div className="tip-card">
            <Icon name="AlertTriangle" size={20} color="#F59E0B" />
            <h5 className="tip-card-title">
              Transparence
            </h5>
            <p className="tip-card-text">
              Montrez clairement tout défaut ou usure
            </p>
          </div>
        </div>
      </div>

      {/* Modal pour afficher l'image en grand */}
      {showImageModal && (
        <div className="image-modal">
          <div className="image-modal-content">
            <div className="image-modal-header">
              <button
                className="modal-close-button"
                onClick={() => setShowImageModal(false)}
              >
                <Icon name="X" size={24} color="white" />
              </button>
              
              <div className="image-info">
                <h4 className="image-name">
                  {selectedPhoto?.fileName || `Photo ${selectedPhotoIndex + 1}`}
                </h4>
                {selectedPhoto?.fileSize && (
                  <span className="image-size">
                    {(selectedPhoto.fileSize / 1024 / 1024).toFixed(1)} Mo
                  </span>
                )}
              </div>
              
              <button
                className="modal-action-button"
                onClick={() => {
                  setShowImageModal(false);
                  openActionMenu(selectedPhoto, selectedPhotoIndex);
                }}
              >
                <Icon name="MoreVertical" size={24} color="white" />
              </button>
            </div>

            <div className="image-modal-body">
              {selectedPhoto && (
                <img
                  src={selectedPhoto.uri}
                  alt={selectedPhoto.alt || 'Photo en plein écran'}
                  className="full-screen-image"
                  onError={() => {
                    alert('Impossible de charger cette image');
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal pour les actions sur la photo */}
      {showActionModal && (
        <div className="action-modal">
          <div className="action-modal-overlay" onClick={() => setShowActionModal(false)} />
          <div className="action-modal-content">
            <div className="action-modal-header">
              <h4 className="action-modal-title">
                Actions Photo
              </h4>
              <button onClick={() => setShowActionModal(false)}>
                <Icon name="X" size={24} color="#6B7280" />
              </button>
            </div>
            
            <div className="action-modal-body">
              <img
                src={selectedPhoto?.uri}
                alt="Preview"
                className="action-modal-image"
              />
              
              <p className="action-photo-name">
                {selectedPhoto?.fileName || `Photo ${selectedPhotoIndex + 1}`}
              </p>
            </div>
            
            <div className="action-modal-actions">
              {selectedPhotoIndex > 0 && !selectedPhoto?.isExisting && (
                <button
                  className="action-button"
                  onClick={() => setPrimaryPhoto(selectedPhotoIndex)}
                >
                  <Icon name="Star" size={20} color="#3B82F6" />
                  <span className="action-button-text">
                    Définir comme principale
                  </span>
                </button>
              )}
              
              {selectedPhotoIndex > 0 && !selectedPhoto?.isExisting && (
                <button
                  className="action-button"
                  onClick={() => reorderPhotos(selectedPhotoIndex, selectedPhotoIndex - 1)}
                >
                  <Icon name="ArrowUp" size={20} color="#6B7280" />
                  <span className="action-button-text">
                    Déplacer vers le haut
                  </span>
                </button>
              )}
              
              {selectedPhotoIndex < allPhotos.length - 1 && !selectedPhoto?.isExisting && (
                <button
                  className="action-button"
                  onClick={() => reorderPhotos(selectedPhotoIndex, selectedPhotoIndex + 1)}
                >
                  <Icon name="ArrowDown" size={20} color="#6B7280" />
                  <span className="action-button-text">
                    Déplacer vers le bas
                  </span>
                </button>
              )}
              
              <button
                className="action-button delete-action-button"
                onClick={() => removePhoto(selectedPhotoIndex)}
              >
                <Icon name="Trash2" size={20} color="#DC2626" />
                <span className="action-button-text delete-action-text">
                  Supprimer la photo
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;